import { createKExecution, stepK } from '../engine/ku'
import { compiledTreeLookupKU, treeLookupInput } from '../programs/tree-build-ku'

const heroInitial = createKExecution(
  treeLookupInput(['R', 'L']),
  compiledTreeLookupKU,
)
const heroAfter = stepK(heroInitial, compiledTreeLookupKU)
const heroTrace = heroAfter.lastTrace

function PaperNode({
  x,
  y,
  small = false,
  initial = false,
  frontier = false,
  label,
  className = '',
}: {
  x: number
  y: number
  small?: boolean
  initial?: boolean
  frontier?: boolean
  label?: string
  className?: string
}) {
  const scale = small ? 0.72 : 1

  return (
    <g
      className={['km-hero-node-group', className].filter(Boolean).join(' ')}
      transform={`translate(${x} ${y}) scale(${scale})`}
    >
      {frontier ? (
        <path
          className="km-hero-frontier-ring"
          d="M-20 -2 C-19 -15 -10 -22 3 -21 C17 -20 23 -10 21 4 C19 17 8 23 -6 20 C-18 17 -23 9 -20 -2Z"
        />
      ) : null}
      <path
        className="km-hero-node"
        d="M-12 -2 C-11 -10 -5 -15 4 -14 C13 -13 17 -5 14 5 C12 13 3 17 -6 13 C-12 10 -15 5 -12 -2Z"
      />
      {initial ? (
        <path
          className="km-hero-initial-ring"
          d="M-7 -2 C-6 -7 -2 -10 3 -9 C8 -8 10 -3 9 3 C7 8 2 10 -3 8 C-7 6 -9 3 -7 -2Z"
        />
      ) : null}
      {label ? (
        <text className="km-hero-node-label" textAnchor="middle" y={small ? 3.3 : 3.7}>
          {label}
        </text>
      ) : null}
    </g>
  )
}

function ExternalFabric() {
  return (
    <g className="km-hero-external">
      <path d="M328 47 C365 40 385 27 410 20 M328 47 C369 53 397 60 436 61" />
      <path d="M363 132 C402 119 431 108 463 102 M363 132 C407 145 439 154 480 156" />
      <path d="M379 234 C409 229 426 222 449 216 M379 234 C418 247 447 258 478 267" />
      <PaperNode x={410} y={20} small />
      <PaperNode x={436} y={61} small />
      <PaperNode x={463} y={102} small />
      <PaperNode x={480} y={156} small />
      <PaperNode x={449} y={216} small label="L" />
      <PaperNode x={478} y={267} small />
    </g>
  )
}

function FrontierPins() {
  return (
    <g>
      <PaperNode x={328} y={47} frontier />
      <PaperNode x={363} y={132} frontier />
      <PaperNode x={252} y={244} frontier label="R" />
      <PaperNode x={379} y={234} frontier />
    </g>
  )
}

function BeforePatch() {
  return (
    <g className="km-hero-patch">
      <path d="M77 154 C111 144 143 126 178 107 M77 158 C112 172 137 188 162 206" />
      <path d="M197 102 C237 80 278 61 316 50 M198 110 C246 119 288 128 350 132" />
      <path d="M181 211 C205 222 226 234 241 241 M182 211 C244 220 306 228 365 234" />
      <PaperNode x={72} y={157} initial label="T0" />
      <PaperNode x={187} y={106} />
      <PaperNode x={171} y={209} />
    </g>
  )
}

function AfterPatch({ forming = false }: { forming?: boolean }) {
  return (
    <g className={forming ? 'km-hero-patch km-hero-patch-forming' : 'km-hero-patch'}>
      <path d="M224 106 C259 83 289 62 318 50 M226 111 C271 120 309 126 352 131" />
      <path d="M290 171 C319 157 339 145 356 136 M292 177 C324 197 349 218 370 231" />
      <PaperNode x={214} y={109} />
      <PaperNode x={281} y={174} initial label="T0" />
    </g>
  )
}

export function HeroMark() {
  const ruleTitle =
    heroTrace?.events.find((event) => event.type === 'rule-applied')?.title
    ?? 'Follow right child'
  const frontierCount = heroTrace?.partition.frontierVertices.length ?? 4

  return (
    <svg
      className="km-hero-mark"
      viewBox="0 0 500 290"
      role="img"
      aria-labelledby="km-hero-mark-title km-hero-mark-desc"
      data-rule-id={heroTrace?.matchedRuleId ?? undefined}
      data-frontier-count={frontierCount}
    >
      <title id="km-hero-mark-title">A K-machine rewrites one typed neighbourhood</title>
      <desc id="km-hero-mark-desc">
        Five slowly cycling frames depict the real first step of the worked tree-address program:
        {` ${ruleTitle}. The interior is removed, ${frontierCount} frontier vertices remain, a new
        local structure is attached, and the consumed R bit is left detached.`}
      </desc>

      <g className="km-hero-frame km-hero-frame-recognise" data-km-hero-frame="recognise">
        <g className="km-hero-ink">
          <ExternalFabric />
          <BeforePatch />
          <FrontierPins />
        </g>
      </g>

      <g className="km-hero-frame km-hero-frame-select" data-km-hero-frame="select">
        <path
          className="km-hero-workfield"
          d="M38 113 C71 62 151 42 235 43 C303 44 347 67 382 121 C405 157 407 213 378 248 C338 269 287 267 251 258 C198 270 119 248 70 207 C38 180 24 144 38 113Z"
        />
        <g className="km-hero-ink">
          <ExternalFabric />
          <BeforePatch />
          <FrontierPins />
        </g>
      </g>

      <g className="km-hero-frame km-hero-frame-remove" data-km-hero-frame="remove">
        <g className="km-hero-ink">
          <ExternalFabric />
          <FrontierPins />
        </g>
        <path className="km-hero-cut" d="M61 145 L78 165 M77 143 L61 166" />
        <path className="km-hero-lifted" d="M93 157 C125 142 151 126 178 108 M94 164 C120 181 141 194 160 203" />
      </g>

      <g className="km-hero-frame km-hero-frame-install" data-km-hero-frame="install">
        <g className="km-hero-ink">
          <ExternalFabric />
          <FrontierPins />
          <AfterPatch forming />
        </g>
        <path className="km-hero-install-stroke" d="M191 80 C222 59 270 54 310 61" />
      </g>

      <g className="km-hero-frame km-hero-frame-continue" data-km-hero-frame="continue">
        <g className="km-hero-ink">
          <ExternalFabric />
          <FrontierPins />
          <AfterPatch />
        </g>
        <path className="km-hero-detached-mark" d="M239 263 C248 270 259 270 268 262" />
      </g>
    </svg>
  )
}
